import { AfterViewInit, Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { GlobalLoaderService } from './global-loader.service';
import { OneQmsAuthService } from './core/auth/one-qms-auth.service';
import { SessionTimeoutService } from './session-timeout-dialog/session-timeout.service';
import { Subject, takeUntil } from 'rxjs';
import { TimeIntegrityService } from './time-integrity.service';
import { SuccessSnackbarComponent } from './support/success-snackbar/success-snackbar.component';
import { messageService } from 'src/app/message.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UAParser } from 'ua-parser-js';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  isLoading = false;
  title = 'angularnew-qms';
  showButton: boolean = false;
  data1: { applicationUrl: string; };

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showButton = window.scrollY > 100;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public button: HTMLElement;
  on: HTMLElement;
  off: HTMLElement;

  ngAfterViewInit() {
    this.on = document.getElementById('on') as HTMLInputElement;
    this.off = document.getElementById('off') as HTMLInputElement;

    globalThis.addEventListener('online', () => {
      this.on.style.display = 'block';
      this.off.style.display = 'none';
      setTimeout(() => { this.on.style.display = 'none'; }, 7000);
    });

    globalThis.addEventListener('offline', () => {
      this.off.style.display = 'block';
      this.on.style.display = 'none';
    });
  }

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly message: messageService,
    private readonly loader: GlobalLoaderService,
    private readonly oneQmsAuthService: OneQmsAuthService,
    private readonly timeIntegrityService: TimeIntegrityService,
    private readonly sessionTimeoutService: SessionTimeoutService) {
    this.loader.getState().subscribe(value => {
      this.isLoading = value;
    });

    console.log(`
  ██╗    ██╗███████╗██╗      ██████╗ ██████╗ ███╗   ███╗███████╗
  ██║    ██║██╔════╝██║     ██╔════╝██╔═══██╗████╗ ████║██╔════╝
  ██║ █╗ ██║█████╗  ██║     ██║     ██║   ██║██╔████╔██║█████╗
  ██║███╗██║██╔══╝  ██║     ██║     ██║   ██║██║╚██╔╝██║██╔══╝
  ╚███╔███╔╝███████╗███████╗╚██████╗╚██████╔╝██║ ╚═╝ ██║███████╗
  ╚══╝╚══╝ ╚══════╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚══════╝

  ████████╗ ██████╗
  ╚══██╔══╝██╔═══██╗
      ██║   ██║   ██║
      ██║   ██║   ██║
      ██║   ╚██████╔╝
      ╚═╝    ╚═════╝

  ██████╗ ███╗   ██╗███████╗ ██████╗ ███╗   ███╗███████╗
  ██╔═══██╗████╗  ██║██╔════╝██╔═══██╗████╗ ████║██╔════╝
  ██║   ██║██╔██╗ ██║█████╗  ██║   ██║██╔████╔██║███████╗
  ██║   ██║██║╚██╗██║██╔══╝  ██║▄▄ ██║██║╚██╔╝██║╚════██║
  ╚██████╔╝██║ ╚████║███████╗╚██████╔╝██║ ╚═╝ ██║███████║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚══▀▀═╝ ╚═╝     ╚═╝╚══════╝

  ══════════════════════════════════════════════════════════════
           🚀 WELCOME TO OneQMS 🚀
  ══════════════════════════════════════════════════════════════
`);
  }

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {

    this.oneQmsAuthService.isLoggedIn$().pipe(takeUntil(this.destroy$)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        console.log('[APP] Authenticated → start session timer');
        this.sessionTimeoutService.start(() => {
          this.oneQmsAuthService.logout('program');
        });

        this.timeIntegrityService.startSilentChecks((type: any) => {
          console.log('CHECKING TIME DRIFTS...')
          if (type == 'severe') {
            this.oneQmsAuthService.logout('time-drift');
          } else {
            this.showSnackBar('System time is out of sync by approximately ' + type.split('+')?.[1] + ' Minutes. Please sync your time automatically to avoid issues and to continue smoothly', 'N')
          }
        });
      } else {
        console.log('[APP] Logged out → stop session timer');
        this.sessionTimeoutService.stop();
        this.timeIntegrityService.stopSilentChecks();
      }
    });

    const parser = new UAParser();
    const result = parser.getResult();

    /* Browser */
    const browserName = result.browser.name || 'Unknown Browser';
    const browserVersion = result.browser.version || '';

    /* OS */
    const osName = result.os.name || 'Unknown OS';
    const osVersion = result.os.version || '';

    /* Device */
    const vendor = result.device.vendor || '';
    const model = result.device.model || '';
    const type = result.device.type || '';

    let deviceName = '';

    /* Mobile / Tablet */
    if (vendor || model) {
      deviceName = `${vendor} ${model}`.trim();
    }
    /* Desktop / Laptop */
    else if (osName.toLowerCase().includes('windows')) {
      deviceName = 'Windows Desktop/Laptop';
    } else if (osName.toLowerCase().includes('mac')) {
      deviceName = 'Mac Desktop/Laptop';
    } else if (osName.toLowerCase().includes('linux')) {
      deviceName = 'Linux Desktop/Laptop';
    } else {
      deviceName = 'Desktop/Laptop';
    }

    /* Final Device Info */
    this.message.deviceInfo = {
      browser: `${browserName} ${browserVersion}`.trim(),
      os: `${osName} ${osVersion}`.trim(),
      deviceType: type || 'Desktop',
      deviceName: deviceName,
      fullDeviceInfo: `${deviceName} | ${browserName} ${browserVersion} | ${osName} ${osVersion}`
    };

    console.log('Device Information → ', this.message.deviceInfo);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  snackbarRef: any;
  showSnackBar(message: string, type: string) {
    let messageType: string;

    if (type === 'S') {
      messageType = 'Success';
    } else if (type === 'E') {
      messageType = 'Error';
    } else {
      messageType = 'Notice';
    }

    this.snackbarRef = this.snackBar.openFromComponent(SuccessSnackbarComponent, {
      duration: type === 'S' ? 5000 : undefined,
      data: {
        message,
        type: messageType,
        buttonLabel: 'Ok',
        pageName: this.message.breadcrumbData.thirdLevel ? this.message.breadcrumbData.thirdLevel : this.message.breadcrumbData.secondLevel,
      },
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: type === 'S' ? 'Success' : 'Error',
    });

    this.snackbarRef.onAction().subscribe(() => {
      this.snackbarRef.dismiss();
    });
  }

}
